import { useEffect, useState } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import { ChartBar, TrendUp, Clock, Package, Lightning, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border-color)", borderRadius: 10, padding: "10px 14px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
      <p style={{ fontFamily: "JetBrains Mono", fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ fontSize: "0.8rem", color: p.color, fontWeight: 600 }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

function StatWidget({ icon: Icon, value, label, color, trend }) {
  return (
    <div className="bento-item p-5 card-hover">
      <div className="flex items-center justify-between mb-3">
        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={22} weight="duotone" style={{ color }} />
        </div>
        {trend && (
          <div className="flex items-center gap-1" style={{ fontSize: "0.7rem", fontWeight: 600, color: trend > 0 ? "var(--success)" : "var(--error)" }}>
            {trend > 0 ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <span style={{ fontFamily: "Outfit", fontSize: "1.8rem", fontWeight: 700, color: "var(--text-main)", display: "block" }}>{value}</span>
      <span className="overline">{label}</span>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const [daily, setDaily] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [dailyRes, summaryRes] = await Promise.all([
          axios.get(`${API}/analytics/daily`),
          axios.get(`${API}/analytics/summary`),
        ]);
        setDaily(dailyRes.data);
        setSummary(summaryRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return <div className="bento-item p-8 text-center"><Clock size={24} style={{ color: "var(--text-muted)", margin: "0 auto" }} /></div>;

  return (
    <div data-testid="analytics-dashboard" className="pt-20 px-6 pb-12 max-w-7xl mx-auto">
      <div className="mb-8">
        <span className="overline" style={{ color: "var(--accent)", display: "block", marginBottom: 4 }}>Analytics</span>
        <h1 style={{ fontFamily: "Outfit", fontSize: "1.8rem", fontWeight: 700 }}>Metricas e Performance</h1>
        <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>Visao geral da rede BluMove nos ultimos 7 dias</p>
      </div>

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="analytics-summary">
          <StatWidget icon={Package} value={summary.total_shipments_week} label="Envios (7 dias)" color="var(--primary)" trend={12} />
          <StatWidget icon={TrendUp} value={summary.total_deliveries_week} label="Entregas (7 dias)" color="var(--success)" trend={8} />
          <StatWidget icon={Clock} value={`${summary.avg_delivery_hours}h`} label="Tempo medio entrega" color="var(--warning)" trend={-5} />
          <StatWidget icon={Lightning} value={`${summary.efficiency_rate}%`} label="Taxa de eficiencia" color="var(--accent)" trend={3} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Shipments chart */}
        <div className="bento-item p-6" data-testid="shipments-chart">
          <div className="flex items-center gap-2 mb-6">
            <ChartBar size={20} weight="duotone" style={{ color: "var(--primary)" }} />
            <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem" }}>Envios vs Entregas</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={daily} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--text-muted)" }} />
              <YAxis tick={{ fontSize: 11, fill: "var(--text-muted)" }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="shipments_created" name="Envios" fill="var(--primary)" radius={[6, 6, 0, 0]} barSize={20} />
              <Bar dataKey="deliveries_completed" name="Entregas" fill="var(--success)" radius={[6, 6, 0, 0]} barSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Delivery time chart */}
        <div className="bento-item p-6" data-testid="delivery-time-chart">
          <div className="flex items-center gap-2 mb-6">
            <Clock size={20} weight="duotone" style={{ color: "var(--warning)" }} />
            <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem" }}>Tempo Medio de Entrega (horas)</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={daily}>
              <defs>
                <linearGradient id="gradTime" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--text-muted)" }} />
              <YAxis tick={{ fontSize: 11, fill: "var(--text-muted)" }} domain={[0, 10]} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="avg_delivery_hours" name="Tempo medio" stroke="var(--accent)" fill="url(#gradTime)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bags in circulation */}
      <div className="bento-item p-6" data-testid="bags-circulation-chart">
        <div className="flex items-center gap-2 mb-6">
          <Package size={20} weight="duotone" style={{ color: "#2563eb" }} />
          <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem" }}>Bolsas em Circulacao</h3>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={daily}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--text-muted)" }} />
            <YAxis tick={{ fontSize: 11, fill: "var(--text-muted)" }} />
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="bags_in_circulation" name="Bolsas" stroke="#2563eb" strokeWidth={2.5} dot={{ fill: "#2563eb", r: 4 }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
